/**
 * Enhanced Command Palette Component
 * 
 * Keyboard-driven command palette for quick actions in HOS.
 * Integrates with HOSContext for state management.
 * Now supports full-screen focus mode expansion.
 * Theme-aware: works with both light and dark modes.
 * Includes speech-to-text for voice commands.
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { Command, Search, X, Keyboard, ChevronRight, Maximize2, Sun, Moon, Check, Mic, MicOff, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { useTheme } from "@/contexts/ThemeContext";
import { toast } from "sonner";

// Icon mapping
const ICONS = {
  "file-text": "📝",
  "brain": "🧠",
  "pill": "💊",
  "users": "👥",
  "clipboard-list": "📋",
  "heart-pulse": "❤️",
  "activity": "📊",
  "phone": "📞",
  "alert-triangle": "⚠️",
  "package": "📦",
  "clipboard-check": "✅",
  "shield": "🛡️",
  "calendar": "📅",
  "stethoscope": "🩺",
  "mic": "🎤",
  "mail": "✉️",
  "bar-chart-3": "📊",
};

// Features that support focus mode expansion
const FOCUS_MODE_FEATURES = [
  "visit_note", "ai_assessment", "medication", "orders", "scheduling",
  "compliance_check", "call_documentation", "idg_discussion", "dme",
  "clinical_automation", "compliance_hub", "volunteer_management",
  "quality_dashboard", "reports", "workforce", "rn_voice_command"
];

export function CommandPalette({
  isOpen,
  onClose,
  commands = [],
  onExecuteCommand,
  contextualSuggestions = [],
  patientName = ""
}) {
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isListening, setIsListening] = useState(false);
  const inputRef = useRef(null);
  const recognitionRef = useRef(null);
  
  // Check speech support immediately (not in effect)
  const speechSupported = typeof window !== 'undefined' && 
    !!(window.SpeechRecognition || window.webkitSpeechRecognition);
  
  // Get theme from context
  const { theme, setTheme: setGlobalTheme, colorScheme, setColorScheme, colorSchemes } = useTheme() || {};
  const isDark = theme === 'dark';

  // Initialize speech recognition
  useEffect(() => {
    if (typeof window !== 'undefined' && speechSupported) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'en-US';
      
      recognitionRef.current.onresult = (event) => {
        const transcript = Array.from(event.results)
          .map(result => result[0].transcript)
          .join('');
        setQuery(transcript);
      };
      
      recognitionRef.current.onend = () => {
        setIsListening(false);
      };
      
      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
        if (event.error === 'not-allowed') {
          toast.error('Microphone access denied');
        }
      };
    }
    
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
    };
  }, [speechSupported]);

  // Toggle speech recognition
  const toggleSpeechRecognition = useCallback(() => {
    if (!speechSupported) {
      toast.error('Speech recognition not supported in this browser');
      return;
    }
    
    if (isListening) {
      recognitionRef.current?.stop();
      setIsListening(false);
    } else {
      try {
        recognitionRef.current?.start();
        setIsListening(true);
        toast.success('Listening... Speak your command');
      } catch (err) {
        console.error('Failed to start speech recognition:', err);
        toast.error('Failed to start voice input');
      }
    }
  }, [isListening, speechSupported]);

  // Stop listening when palette closes
  useEffect(() => {
    if (!isOpen && isListening && recognitionRef.current) {
      recognitionRef.current.stop();
    }
  }, [isOpen, isListening]);

  // Toggle theme
  const toggleTheme = () => {
    if (setGlobalTheme) {
      setGlobalTheme(isDark ? 'light' : 'dark');
    }
  };

  // Show color scheme selector
  const [showColorPicker, setShowColorPicker] = useState(false);

  // Handle command execution with focus mode support
  const handleExecute = (cmd) => {
    // Check if this command supports focus mode
    const supportsFocusMode = FOCUS_MODE_FEATURES.some(f => 
      cmd.id?.includes(f) || cmd.action?.includes(f)
    );

    // Dispatch focus mode event for supported features
    if (supportsFocusMode) {
      window.dispatchEvent(new CustomEvent("commandpalette:execute", {
        detail: {
          command: cmd,
          featureId: cmd.id,
          title: cmd.name,
          icon: ICONS[cmd.icon] || "⚡"
        }
      }));
    }

    // Execute the command normally
    onExecuteCommand(cmd);
  };

  // Filter commands based on query
  const filteredCommands = query.trim()
    ? commands.filter(cmd =>
        (cmd.name || cmd.label || '')?.toLowerCase().includes(query.toLowerCase()) ||
        cmd.keywords?.some(k => k?.toLowerCase().includes(query.toLowerCase())) ||
        (cmd.description || '')?.toLowerCase().includes(query.toLowerCase())
      )
    : commands.slice(0, 10);

  // Handle query change - reset selection
  const handleQueryChange = (newQuery) => {
    setQuery(newQuery);
    setSelectedIndex(0);
  };

  // Track previous isOpen state
  const prevIsOpenRef = useRef(isOpen);

  // Focus input and reset query when opened
  useEffect(() => {
    // Only reset when transitioning from closed to open
    if (isOpen && !prevIsOpenRef.current && inputRef.current) {
      inputRef.current.focus();
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen]);
  
  // Reset state when closed
  useEffect(() => {
    if (!isOpen) {
      // Reset on next open
      setTimeout(() => {
        setQuery("");
        setSelectedIndex(0);
      }, 200);
    }
  }, [isOpen]);

  // Listen for voice command palette events
  useEffect(() => {
    const handleVoiceCommand = (event) => {
      const { commandId, label } = event.detail || {};
      
      if (commandId) {
        // Find the matching command
        const matchingCommand = commands.find(cmd => 
          cmd.id === commandId || 
          cmd.name?.toLowerCase().includes(commandId.toLowerCase()) ||
          cmd.keywords?.some(kw => kw.toLowerCase().includes(commandId.toLowerCase()))
        );
        
        if (matchingCommand) {
          // Execute the command
          handleExecute(matchingCommand);
          onClose();
        } else {
          // If no exact match, set the query to filter
          setQuery(label || commandId);
        }
      }
    };

    window.addEventListener("voicecommand:palette", handleVoiceCommand);
    return () => window.removeEventListener("voicecommand:palette", handleVoiceCommand);
  }, [commands, onClose]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, filteredCommands.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, 0));
      } else if (e.key === "Enter" && filteredCommands[selectedIndex]) {
        e.preventDefault();
        handleExecute(filteredCommands[selectedIndex]);
        onClose();
      } else if (e.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, filteredCommands, selectedIndex, onExecuteCommand, onClose]);

  if (!isOpen) return null;

  // Group commands by category
  const groupedCommands = filteredCommands.reduce((acc, cmd) => {
    const category = cmd.category || "other";
    if (!acc[category]) acc[category] = [];
    acc[category].push(cmd);
    return acc;
  }, {});

  const categoryLabels = {
    documentation: "Documentation",
    clinical: "Clinical",
    medication: "Medication",
    communication: "Communication",
    compliance: "Compliance",
    orders: "Orders",
    navigation: "Navigation",
    admin: "Admin",
    ai: "AI & Intelligence",
    tools: "Tools",
    settings: "Settings",
    other: "Other"
  };

  return (
    <div 
      className={`fixed inset-0 z-50 flex items-center justify-center ${
        isDark ? 'bg-black/70' : 'bg-black/50'
      } backdrop-blur-sm`}
      onClick={onClose}
      data-testid="command-palette-backdrop"
    >
      <div 
        className={`${
          isDark 
            ? 'bg-slate-900 border-slate-700' 
            : 'bg-white border-gray-200'
        } border rounded-xl shadow-2xl w-[95vw] h-[90vh] max-w-6xl overflow-hidden animate-in fade-in zoom-in-95 duration-200 flex flex-col`}
        onClick={e => e.stopPropagation()}
        data-testid="command-palette"
      >
        {/* Search Header */}
        <div className={`flex items-center gap-3 px-6 py-4 border-b ${
          isDark ? 'border-slate-700' : 'border-gray-200'
        } shrink-0`}>
          <Search className={`w-6 h-6 ${isDark ? 'text-slate-400' : 'text-gray-400'}`} />
          <Input
            ref={inputRef}
            value={query}
            onChange={(e) => handleQueryChange(e.target.value)}
            placeholder={patientName ? `Actions for ${patientName}...` : "Search commands or use 🎤 voice..."}
            className={`flex-1 bg-transparent border-none text-lg focus-visible:ring-0 h-10 px-0 ${
              isDark 
                ? 'text-white placeholder:text-slate-500' 
                : 'text-gray-900 placeholder:text-gray-400'
            }`}
            data-testid="command-search-input"
          />
          {/* Voice Input Button */}
          {speechSupported && (
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleSpeechRecognition}
              className={`relative ${
                isListening 
                  ? 'text-red-500 hover:text-red-400 bg-red-500/10' 
                  : isDark 
                    ? 'text-teal-400 hover:text-teal-300 hover:bg-slate-800' 
                    : 'text-teal-600 hover:text-teal-500 hover:bg-gray-100'
              }`}
              title={isListening ? "Stop listening" : "Voice search (click to speak)"}
              data-testid="voice-search-button"
            >
              {isListening ? (
                <>
                  <MicOff className="w-5 h-5" />
                  <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                </>
              ) : (
                <Mic className="w-5 h-5" />
              )}
            </Button>
          )}
          {/* Theme Toggle Button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleTheme}
            className={`${isDark ? 'text-slate-400 hover:text-white hover:bg-slate-800' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'}`}
            data-testid="theme-toggle-btn"
          >
            {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </Button>
          {/* Color Scheme Button */}
          <div className="relative">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowColorPicker(!showColorPicker)}
              className={`${isDark ? 'text-slate-400 hover:text-white hover:bg-slate-800' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'}`}
              data-testid="color-scheme-btn"
            >
              <div 
                className="w-5 h-5 rounded-full border-2 border-white/30"
                style={{ backgroundColor: colorSchemes?.[colorScheme]?.primary || '#0D9488' }}
              />
            </Button>
            {/* Color Picker Dropdown */}
            {showColorPicker && colorSchemes && (
              <div 
                className={`absolute right-0 top-full mt-2 p-3 rounded-lg shadow-xl z-50 ${
                  isDark ? 'bg-slate-800 border border-slate-700' : 'bg-white border border-gray-200'
                }`}
                style={{ width: '240px' }}
              >
                <p className={`text-xs font-medium mb-2 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                  Color Theme
                </p>
                <div className="grid grid-cols-4 gap-2">
                  {Object.entries(colorSchemes).map(([key, scheme]) => (
                    <button
                      key={key}
                      onClick={() => {
                        setColorScheme(key);
                        setShowColorPicker(false);
                      }}
                      className={`w-10 h-10 rounded-lg transition-all relative ${
                        colorScheme === key 
                          ? 'ring-2 ring-offset-2 ring-offset-background' 
                          : 'hover:scale-110'
                      }`}
                      style={{ 
                        backgroundColor: scheme.primary,
                        ringColor: scheme.primary 
                      }}
                      title={scheme.name}
                      data-testid={`color-picker-${key}`}
                    >
                      {colorScheme === key && (
                        <Check className="w-4 h-4 text-white absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className={`flex items-center gap-2 text-sm ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
            <kbd className={`px-2 py-1 rounded ${isDark ? 'bg-slate-800 text-slate-400' : 'bg-gray-100 text-gray-600'}`}>↑↓</kbd>
            <span>navigate</span>
            <kbd className={`px-2 py-1 rounded ml-2 ${isDark ? 'bg-slate-800 text-slate-400' : 'bg-gray-100 text-gray-600'}`}>↵</kbd>
            <span>select</span>
            <kbd className={`px-2 py-1 rounded ml-2 ${isDark ? 'bg-slate-800 text-slate-400' : 'bg-gray-100 text-gray-600'}`}>ESC</kbd>
            <span>close</span>
          </div>
        </div>

        {/* Contextual Suggestions */}
        {contextualSuggestions.length > 0 && !query && (
          <div className={`px-6 py-3 border-b shrink-0 ${
            isDark 
              ? 'border-slate-700/50 bg-teal-900/20' 
              : 'border-gray-200 bg-teal-50'
          }`}>
            <p className={`text-sm mb-2 flex items-center gap-1 ${
              isDark ? 'text-teal-400' : 'text-teal-700'
            }`}>
              <span className="w-2 h-2 rounded-full bg-teal-500 animate-pulse" />
              Suggested for this patient
            </p>
            <div className="flex flex-wrap gap-2">
              {contextualSuggestions.map((cmd, idx) => (
                <Badge
                  key={idx}
                  className={`cursor-pointer px-3 py-1 text-sm ${
                    isDark 
                      ? 'bg-teal-600/30 hover:bg-teal-600/50 text-teal-300 border border-teal-500/30' 
                      : 'bg-teal-100 hover:bg-teal-200 text-teal-800 border border-teal-300'
                  }`}
                  onClick={() => {
                    handleExecute(cmd);
                    onClose();
                  }}
                >
                  {cmd.name}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Command List */}
        <ScrollArea className="flex-1 min-h-0">
          {filteredCommands.length > 0 ? (
            <div className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {Object.entries(groupedCommands).map(([category, cmds]) => (
                  <div key={category} className="space-y-2">
                    <p className={`text-sm px-2 py-1 uppercase tracking-wider font-medium border-b pb-2 ${
                      isDark 
                        ? 'text-slate-500 border-slate-800' 
                        : 'text-gray-500 border-gray-200'
                    }`}>
                      {categoryLabels[category] || category}
                    </p>
                    {cmds.map((cmd, idx) => {
                      const globalIdx = filteredCommands.indexOf(cmd);
                      const isSelected = globalIdx === selectedIndex;
                      const supportsFocusMode = FOCUS_MODE_FEATURES.some(f => 
                        cmd.id?.includes(f) || cmd.action?.includes(f)
                      );
                      const isDisabled = cmd.disabled || cmd.comingSoon;
                      
                      return (
                        <div
                          key={cmd.id}
                          className={`flex items-center justify-between px-4 py-3 rounded-lg transition-colors ${
                            isDisabled 
                              ? "opacity-50 cursor-not-allowed border border-transparent"
                              : isSelected 
                                ? isDark
                                  ? "bg-teal-600/20 border border-teal-500/30 cursor-pointer"
                                  : "bg-teal-100 border border-teal-300 cursor-pointer"
                                : isDark
                                  ? "hover:bg-slate-800 border border-transparent cursor-pointer"
                                  : "hover:bg-gray-100 border border-transparent cursor-pointer"
                          }`}
                          onClick={() => {
                            if (!isDisabled) {
                              handleExecute(cmd);
                              onClose();
                            }
                          }}
                          data-testid={`command-item-${cmd.id}`}
                        >
                          <div className="flex items-center gap-3">
                            <span className={`text-xl flex-shrink-0 ${isDisabled ? "grayscale" : ""}`}>{ICONS[cmd.icon] || "⚡"}</span>
                            <div className="min-w-0 flex-1">
                              <p className={`font-medium truncate ${
                                isSelected && !isDisabled 
                                  ? isDark ? "text-white" : "text-gray-900"
                                  : isDisabled 
                                    ? isDark ? "text-slate-500" : "text-gray-400"
                                    : isDark ? "text-slate-100" : "text-gray-800"
                              }`}
                              style={{ 
                                color: isSelected && !isDisabled 
                                  ? (isDark ? '#ffffff' : '#111827')
                                  : isDisabled 
                                    ? (isDark ? '#64748b' : '#9ca3af')
                                    : (isDark ? '#f1f5f9' : '#1f2937'),
                                visibility: 'visible',
                                opacity: 1
                              }}
                              >
                                {cmd.name || cmd.label || cmd.id || 'Unknown Command'}
                                {supportsFocusMode && !isDisabled && (
                                  <Maximize2 className={`inline-block w-3 h-3 ml-2 opacity-60 ${
                                    isDark ? 'text-teal-400' : 'text-teal-600'
                                  }`} />
                                )}
                                {isDisabled && (
                                  <Badge className={`ml-2 text-[10px] px-1.5 py-0 ${
                                    isDark 
                                      ? 'bg-slate-600 text-slate-300' 
                                      : 'bg-gray-200 text-gray-500'
                                  }`}>Coming Soon</Badge>
                                )}
                              </p>
                              {(cmd.description || cmd.name || cmd.label) && (
                                <p className={`text-xs line-clamp-1 ${
                                  isDisabled 
                                    ? isDark ? "text-slate-600" : "text-gray-400"
                                    : isDark ? "text-slate-300" : "text-gray-600"
                                }`}
                                style={{
                                  color: isDisabled 
                                    ? (isDark ? '#475569' : '#9ca3af')
                                    : (isDark ? '#cbd5e1' : '#4b5563'),
                                  visibility: 'visible',
                                  opacity: 1
                                }}
                                >{cmd.description || `Open ${cmd.name || cmd.label || cmd.id}`}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {cmd.shortcut && !isDisabled && (
                              <kbd className={`px-2 py-1 rounded text-xs ${
                                isDark 
                                  ? 'bg-slate-800 text-slate-400 border border-slate-700' 
                                  : 'bg-gray-100 text-gray-600 border border-gray-200'
                              }`}>
                                {cmd.shortcut}
                              </kbd>
                            )}
                            <ChevronRight className={`w-4 h-4 ${
                              isSelected 
                                ? isDark ? "text-teal-400" : "text-teal-600"
                                : isDark ? "text-slate-600" : "text-gray-400"
                            }`} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className={`flex flex-col items-center justify-center py-24 ${
              isDark ? 'text-slate-400' : 'text-gray-500'
            }`}>
              <Search className="w-12 h-12 mb-4 opacity-50" />
              <p className="text-lg">No commands found for &quot;{query}&quot;</p>
              <p className="text-sm mt-2">Try a different search term</p>
            </div>
          )}
        </ScrollArea>

        {/* Footer */}
        <div className={`px-6 py-3 border-t flex items-center justify-between text-sm shrink-0 ${
          isDark 
            ? 'border-slate-700 bg-slate-800/30 text-slate-500' 
            : 'border-gray-200 bg-gray-50 text-gray-500'
        }`}>
          <div className="flex items-center gap-2">
            <Keyboard className="w-4 h-4" />
            <span>Press</span>
            <kbd className={`px-2 py-1 rounded ${isDark ? 'bg-slate-800' : 'bg-gray-200'}`}>⌘K</kbd>
            <span>to open anytime</span>
          </div>
          <div className="flex items-center gap-4">
            <span className={isDark ? 'text-slate-600' : 'text-gray-400'}>|</span>
            <span>{filteredCommands.length} commands available</span>
            <span className={isDark ? 'text-slate-600' : 'text-gray-400'}>|</span>
            <span className="flex items-center gap-1">
              {isDark ? <Moon className="w-3 h-3" /> : <Sun className="w-3 h-3" />}
              {isDark ? 'Dark' : 'Light'} Mode
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CommandPalette;
